<template lang="pug">
  ProgramSettingsFormWrapper#welcome-card(
    :form-data="formData"
    :disable-save.sync="disableSave"
    :keys-to-reset="keysToReset"
    title="Program Settings"
  )
    ContentLoader(:width="300" :height="120" v-if="!programLoaded")
      rect(x="0", y="0", rx="4", ry="4", width="45%", height="10")
      rect(x="0", y="12", rx="4", ry="4", width="20%", height="20")
      rect(x="55%", y="0", rx="4", ry="4", width="45%", height="10")
      rect(x="55%", y="12", rx="4", ry="4", width="20%", height="20")

      rect(x="0", y="42", rx="4", ry="4", width="45%", height="10")
      rect(x="0", y="54", rx="4", ry="4", width="20%", height="20")
      rect(x="55%", y="42", rx="4", ry="4", width="45%", height="10")
      rect(x="55%", y="54", rx="4", ry="4", width="20%", height="20")

      rect(x="0", y="84", rx="4", ry="4", width="45%", height="10")
      rect(x="0", y="96", rx="4", ry="4", width="20%", height="20")
      rect(x="55%", y="84", rx="4", ry="4", width="45%", height="10")
      rect(x="55%", y="96", rx="4", ry="4", width="20%", height="20")
    template(v-else)
      .columns.is-multiline.is-variable
        .column.is-half
          WtSwitchQuestionInput(
            label="Enforce Step Goal"
            :tooltip-text="enforceStepGoalTooltip"
            v-model="enforceStepGoal"
            @input="handleEnforceStepGoal($event)"
            name="enforce step goal"
          )
          br
          BField(
            v-if="enforceStepGoal"
            label="Default step goal"
            label-for="defaultStepGoal"
            :type="{ 'is-danger': stepGoalRequiredMessage }"
            :message="stepGoalRequiredMessage || ''"
          )
            BInput#defaultStepGoal(
              name="default step goal"
              v-model="defaultStepGoal"
              placeholder="8,000"
              @input="handleDefaultStepGoalInput($event)"
            )
        .column.is-half
          WtRadioQuestionInput(
            label="Distance Unit of Measurement"
          )
            template(v-slot:radioOne)
              BRadio.define-radio(
                  v-model="units"
                  native-value="Miles"
                  @input="disableSave = false"
                )
                  | Miles
            template(v-slot:radioTwo)
              BRadio.define-radio(
                  v-model="units"
                  native-value="Kilometers"
                  @input="disableSave = false"
                )
                  | Kilometers
        .column.is-half
          WtSwitchQuestionInput(
            label="Weekly stats email"
            :tooltip-text="weeklyStatsEmailsTooltip"
            v-model="weeklyStatsEmails"
            @input="disableSave = false"
          )
        .column.is-half
          WtSwitchQuestionInput(
            label="Manual Entry"
            :tooltip-text="manualEntryTooltip"
            v-model="manualEntry"
            @input="disableSave = false"
          )
        .column.is-half
          WtSwitchQuestionInput(
            label="Hide Real Member Names"
            :tooltip-text="showRealNamesTooltip"
            v-model="hideRealNames"
            @input="disableSave = false"
          )
        .column.is-half
          WtSwitchQuestionInput(
            label="Language Translator"
            v-model="languageTranslator"
            @input="disableSave = false"
          )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import programSettingsFormMixin from "@/mixins/programSettingsFormMixin";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import { ContentLoader } from "vue-content-loader";

export default {
  name: "Settings",

  components: {
    ProgramSettingsFormWrapper,
    ContentLoader,
    WtSwitchQuestionInput,
    WtRadioQuestionInput
  },

  mixins: [programSettingsFormMixin, numberUtilityMixin],

  data() {
    return {
      stepGoalRequiredMessage: "",
      enforceStepGoalTooltip: `
      This setting sets a program-wide step goal. If this setting is turned off,
      members are allowed to select their own step goal.
      `,
      manualEntryTooltip: `
      Allow members who don't have an activity-tracking device or who have a
      non-wireless pedometer to manually enter their steps.
      `,
      weeklyStatsEmailsTooltip: `
      Allow Be Well to send out a once-weekly email to members with an
      individual's stats and progress over the course of the week.
      `,
      showRealNamesTooltip: `Hide first and last names from program members.
      Program members can only see usernames of other participants.`
    };
  },

  computed: {
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programLoaded"
    ]),

    formData() {
      return {
        manualEntry: this.manualEntry,
        languageTranslator: this.languageTranslator,
        units: this.units,
        defaultStepGoal: this.defaultStepGoal
          ? this.sanitizeNum(this.defaultStepGoal)
          : 0,
        enforceStepGoal: this.enforceStepGoal,
        weeklyStatsEmails: this.weeklyStatsEmails,
        showRealNames: this.hideRealNames === 1 ? 0 : 1
      };
    },

    keysToReset() {
      return [
        "manualEntry",
        "languageTranslator",
        "units",
        "defaultStepGoal",
        "enforceStepGoal",
        "weeklyStatsEmails"
      ];
    },

    languageTranslator: {
      set(languageTranslator) {
        this.updateProgramSettingsCopy({
          key: "languageTranslator",
          value: languageTranslator
        });
      },
      get() {
        return Number(this.programSettingsCopy.languageTranslator);
      }
    },

    manualEntry: {
      set(manualEntry) {
        this.updateProgramSettingsCopy({
          key: "manualEntry",
          value: manualEntry
        });
      },
      get() {
        return Number(this.programSettingsCopy.manualEntry);
      }
    },

    units: {
      set(units) {
        this.updateProgramSettingsCopy({
          key: "units",
          value: units
        });
      },
      get() {
        return this.programSettingsCopy.units;
      }
    },

    enforceStepGoal: {
      set(enforceStepGoal) {
        this.updateProgramSettingsCopy({
          key: "enforceStepGoal",
          value: enforceStepGoal
        });
        if (!enforceStepGoal) {
          this.updateProgramSettingsCopy({
            key: "defaultStepGoal",
            value: 6000
          });
        }
      },
      get() {
        return Number(this.programSettingsCopy.enforceStepGoal);
      }
    },

    weeklyStatsEmails: {
      set(weeklyStatsEmails) {
        this.updateProgramSettingsCopy({
          key: "weeklyStatsEmails",
          value: weeklyStatsEmails
        });
      },
      get() {
        return Number(this.programSettingsCopy.weeklyStatsEmails);
      }
    },

    defaultStepGoal: {
      set(defaultStepGoal) {
        this.updateProgramSettingsCopy({
          key: "defaultStepGoal",
          value: defaultStepGoal
        });
      },
      get() {
        let goal = "";

        if (this.programSettingsCopy.defaultStepGoal) {
          goal = this.formatWithCommas(
            this.programSettingsCopy.defaultStepGoal
          );
        }

        return goal;
      }
    },

    hideRealNames: {
      set(hideRealNames) {
        this.updateProgramSettingsCopy({
          key: "showRealNames",
          value: hideRealNames === 1 ? 0 : 1
        });
      },
      get() {
        return Number(this.programSettingsCopy.showRealNames) === 1 ? 0 : 1;
      }
    }
  },

  methods: {
    ...mapActions("program", ["updateProgramSettingsCopy"]),

    setGoalRequiredMessage() {
      this.stepGoalRequiredMessage =
        "The default step goal field is required when enforce step goal is on.";
    },

    handleDefaultStepGoalInput(goal) {
      this.disableSave = this.isOriginalValue("defaultStepGoal", goal);

      if (!goal && this.enforceStepGoal) {
        this.setGoalRequiredMessage();
        this.disableSave = true;
      } else {
        this.stepGoalRequiredMessage = "";
      }
    },

    handleEnforceStepGoal(enforce) {
      if (enforce && !this.defaultStepGoal) {
        this.setGoalRequiredMessage();
        this.disableSave = true;
      } else {
        this.stepGoalRequiredMessage = "";
        this.disableSave = false;
      }
    }
  }
};
</script>

<style lang="sass" scoped></style>
